import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faChevronCircleLeft, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import ReactTagInput from "@pathofdev/react-tag-input";
import {
  PageContainer,
  SectionTitle,
  Button,
  Table,
  TableCell,
  TableHeader,
  TableFormGroup,
  Divider,
  TableRow,
  fadeIn,
  Footer,
  useGoBack,
  FormRow,
  FormGroup,
  Label,
  TagInputWrapper,
  Input,
  FormRowFull,
  Select,
} from '../styles/PageStyles';
import { useAuth } from '../context/AuthContext';
import { createPersona, getPersonas, updatePersona } from '../services/personaService';
import { useToast } from '../components/common/ToastProvider';

const PersonaDetails = () => {
  const { personaId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const {showToast} = useToast();

  const initialPersona = {
    id: null,
    code: "PER-" + new Date().getTime(),
    name: '',
    company_cd: null,
    description: '',
    type: '',
    status: 'ACTIVE',
    avatar: '/assets/images/personas/claims-assistant.png',
    personality: {
      tone: 'Professional',
      style: 'Helpful',
      empathyLevel: 'High',
      formality: 'Moderate',
      traits: ['Efficient', 'Detail-oriented', 'Patient'],
    },
    capabilities: [
      {
        id: '',
        name: '',
        description: '',
        enabled: true,
      },
    ],
    knowledgeSources: [
      {
        id: '',
        name: '',
        type: '',
        status: '',
      },
    ],
    promptTemplate: {
      id: '',
      name: '',
      content: '',
      variables: [],
    },
    performance: {
      accuracy: 0,
      responseTime: 0,
      satisfactionScore: 0,
      completionRate: 0,
      lastEvaluated: '',
    },
    settings: {
      maxResponseLength: 1000,
      responseFormat: '',
      allowedActions: [''],
      accessLevel: '',
      modelId: '',
      temperature: 0.7,
      maxTokens: 2048,
    },
    integrations: [
      {
        id: '',
        name: '',
        type: '',
        status: '',
      },
    ],
    version: '',
    category: '',
    tags: [],
    skills: [],
    enabled:true,
    creator: {
      id: '',
      name: '',
      role: '',
    },
  };

  const [personaDetail, setPersonaDetail] = useState(initialPersona);

  useEffect(() => {
    const fetchPersonaById = async () => {
      try {
        let filter = {
          code: personaId
        }
        const response = await getPersonas(filter);
        setPersonaDetail(response[0]);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    if (personaId != 'new') {
      fetchPersonaById();
    } else {
      if (user) {
        initialPersona.company_cd = user.companyCd;
        setPersonaDetail(initialPersona);
      }
    }
  }, [personaId, user]);


  const handleTagChange = (field, tags) => {
    setPersonaDetail({ ...personaDetail, [field]: tags });
  };



  const handleFieldChange = (field, value) => {
    setPersonaDetail({ ...personaDetail, [field]: value });
  };

  const handleNestedFieldChange = (parentField, field, value) => {
    setPersonaDetail({
      ...personaDetail,
      [parentField]: { ...personaDetail[parentField], [field]: value },
    });
  };

  const handleArrayFieldChange = (field, index, key, value) => {
    const updatedArray = [...personaDetail[field]];
    updatedArray[index][key] = value;
    setPersonaDetail({ ...personaDetail, [field]: updatedArray });
  };

  const addArrayFieldItem = (field, template) => {
    const updatedArray = [...(personaDetail[field] || []), template];
    setPersonaDetail({ ...personaDetail, [field]: updatedArray });
  };

  const removeArrayFieldItem = (field, index) => {
    const updatedArray = personaDetail[field].filter((_, i) => i !== index);
    setPersonaDetail({ ...personaDetail, [field]: updatedArray });
  };

  const savePersona = () => {
    try {
      if (personaDetail.id) {
        updatePersona(personaDetail);
      } else {
        createPersona(personaDetail);
      }
      showToast({
        message:'Personas updated successfully.',
        type:'success'
      })
      navigate('/personas');
    } catch (error) {
      console.error('Error saving persona:', error);

    }
  };


  return (
    <PageContainer>
      <motion.div initial="hidden" animate="visible" variants={fadeIn}>
        <SectionTitle>
          <Button onClick={goBack} secondary>
            <FontAwesomeIcon icon={faChevronCircleLeft} />
          </Button>
          <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            {personaDetail?.name || 'Add Persona'}
          </span>
        </SectionTitle>
        <Divider />

        {/* Scalar Fields */}
        <FormRow>
          {/* <FormGroup>
            <Label>Code</Label>
            <Input
              type="text"
              value={personaDetail.code}
              onChange={(e) => handleFieldChange('code', e.target.value)}
            />
          </FormGroup> */}
          <FormGroup>
            <Label>Name</Label>
            <Input
              type="text"
              value={personaDetail.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>Category</Label>
            <Input
              type="text"
              value={personaDetail.category}
              onChange={(e) => handleFieldChange('category', e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>Tags</Label>
            <TagInputWrapper>
              <ReactTagInput
                tags={personaDetail.tags || []}
                onChange={(newTags) => handleTagChange('tags', newTags)}
              />
            </TagInputWrapper>
          </FormGroup>
          
          <FormGroup>
            <Label>Skills</Label>
            <TagInputWrapper>
              <ReactTagInput
                tags={personaDetail.skills || []}
                onChange={(newTags) => handleTagChange('skills', newTags)}
              />
            </TagInputWrapper>
          </FormGroup>
          <FormGroup>
            <Label>Enabled</Label>
            <Select
              value={personaDetail?.enabled || ''}
              onChange={(e) => handleFieldChange('enabled', e.target.value)

              }
            >
              <option value="">Status</option>
              <option value='Active'>Active</option>
              <option value='Inactive'>In Active</option>
            </Select>
          </FormGroup>
        </FormRow>
        <FormRowFull>
          <FormGroup>
            <Label>Description</Label>
            <Input
              type="text"
              value={personaDetail.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
            />
          </FormGroup>
        </FormRowFull>

        {/* Nested Object: Personality */}
        <SectionTitle>Personality</SectionTitle>
        <FormRow>
          {Object.keys(personaDetail.personality).map((key) => (

            <FormGroup key={key}>
              <Label>{key}</Label>
              <Input
                type="text"
                value={personaDetail.personality[key]}
                onChange={(e) =>
                  handleNestedFieldChange('personality', key, e.target.value)
                }
              />
            </FormGroup>

          ))}
        </FormRow>
        {/* Array Fields: Capabilities */}
        <SectionTitle style={{ display: 'flex', margin: '10px auto', borderBottom: "1px solid #666", paddingBottom: '10px', justifyContent: 'space-between' }}>Capabilities

          <Button
            onClick={() =>
              addArrayFieldItem('capabilities', {
                id: '',
                name: '',
                description: '',
                enabled: true,
              })
            }
          >
            <FontAwesomeIcon icon={faPlus} /> Add Capability
          </Button>
        </SectionTitle>
        {personaDetail.capabilities.map((capability, index) => (
          <div key={index}>
            <FormRowFull style={{ display: 'flex', width: '100%', }}>
              <Input
                type="text"
                placeholder='Name'
                value={capability.name}
                onChange={(e) =>
                  handleArrayFieldChange('capabilities', index, 'name', e.target.value)
                }
              />
              <Input
                type="text"
                placeholder='Description'
                value={capability.description}
                onChange={(e) =>
                  handleArrayFieldChange('capabilities', index, 'description', e.target.value)
                }
              />
              <Select
                value={capability?.enabled || ''}
                onChange={(e) => handleArrayFieldChange('capability', index, 'enabled', e.target.value)

                }
              >
                <option value="">Status</option>
                <option value={true}>Yes</option>
                <option value={false}>No</option>
              </Select>
              <Button
                onClick={() => removeArrayFieldItem('capabilities', index)}
                style={{ marginBottom: '0px', minWidth: '100px' }}
              >
                <FontAwesomeIcon icon={faTrash} /> Remove
              </Button>
            </FormRowFull>

          </div>
        ))}
        <Divider />
        {/* Array Fields: Knowledge Sources */}
        <SectionTitle style={{ display: 'flex', margin: '10px auto', borderBottom: "1px solid #666", paddingBottom: '10px', justifyContent: 'space-between' }}>Knowledge Sources

          <Button
            onClick={() =>
              addArrayFieldItem('knowledgeSources', {
                id: '',
                name: '',
                type: '',
                status: '',
              })
            }
          >
            <FontAwesomeIcon icon={faPlus} /> Add Knowledge Source
          </Button>
        </SectionTitle>
        {personaDetail.knowledgeSources.map((source, index) => (
          <div key={index}>
            <FormRowFull style={{ display: 'flex', width: '100%', }}>
              <Input
                type="text"
                placeholder='Name'
                value={source.name}
                onChange={(e) =>
                  handleArrayFieldChange('knowledgeSources', index, 'name', e.target.value)
                }
              />
              <Input
                type="text"
                placeholder='Type'
                value={source.type}
                onChange={(e) =>
                  handleArrayFieldChange('knowledgeSources', index, 'type', e.target.value)
                }
              />
              <Select
                placeholder='Status'
                value={source?.status || ''}
                onChange={(e) => handleArrayFieldChange('knowledgeSources', index, 'status', e.target.value)

                }
              >
                <option value="">Status</option>
                <option value='ACTIVE'>Active</option>
                <option value="INACTIVE">Inactive</option>
              </Select>
              <Button
                onClick={() => removeArrayFieldItem('knowledgeSources', index)}
                style={{ marginBottom: '0', minWidth: '100px' }}
              >
                <FontAwesomeIcon icon={faTrash} /> Remove
              </Button>
            </FormRowFull>
          </div>
        ))}
        <Divider />

        {/* Array Fields: Integrations */}
        <SectionTitle style={{ display: 'flex', margin: '10px auto', borderBottom: "1px solid #666", paddingBottom: '10px', justifyContent: 'space-between' }}>Integrations
          <Button
            onClick={() =>
              addArrayFieldItem('integrations', {
                id: '',
                name: '',
                type: '',
                status: '',
              })
            }
          >
            <FontAwesomeIcon icon={faPlus} /> Add Integrations
          </Button>
        </SectionTitle>
        {personaDetail.integrations.map((integration, index) => (
          <div key={index}>
            <FormRowFull style={{ display: 'flex', width: '100%', }}>
              <Input
                type="text"
                placeholder='Name'
                value={integration.name}
                onChange={(e) =>
                  handleArrayFieldChange('integrations', index, 'name', e.target.value)
                }
              />
              <Input
                type="text"
                placeholder='Type'
                value={integration.type}
                onChange={(e) =>
                  handleArrayFieldChange('integrations', index, 'type', e.target.value)
                }
              />
              <Select
                placeholder='Status'
                value={integration?.status || ''}
                onChange={(e) => handleArrayFieldChange('integrations', index, 'status', e.target.value)

                }
              >
                <option value="">Status</option>
                <option value='Connected'>Connected</option>
                <option value="Not-Connected">Not-Connected</option>
              </Select>

              <Button
                onClick={() => removeArrayFieldItem('integrations', index)}
                style={{ marginBottom: '0px' }}
              >
                <FontAwesomeIcon icon={faTrash} /> Remove
              </Button>
            </FormRowFull>
          </div>
        ))}
        <Divider />
        {/* Nested Object: Prompt Template */}
        <SectionTitle>Prompt Template</SectionTitle>
        <FormRow>
          <FormGroup>
            <Label>Name</Label>
            <Input
              type="text"
              value={personaDetail.promptTemplate.name}
              onChange={(e) =>
                handleNestedFieldChange('promptTemplate', 'name', e.target.value)
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>Variables</Label>

            <TagInputWrapper>
              <ReactTagInput
                tags={personaDetail.promptTemplate.variables || []}
                onChange={(e) => {
                  handleNestedFieldChange('promptTemplate', 'variables', e)
                }}
              />
            </TagInputWrapper>
          </FormGroup>
        </FormRow>
        <FormRowFull>
          <FormGroup>
            <Label>Content</Label>
            <Input
              type="text"
              value={personaDetail.promptTemplate.content}
              onChange={(e) =>
                handleNestedFieldChange('promptTemplate', 'content', e.target.value)
              }
            />
          </FormGroup>
        </FormRowFull>


        <Footer>
          <Button primary onClick={savePersona}>
            <FontAwesomeIcon icon={faSave} /> Save Persona
          </Button>
        </Footer>
      </motion.div>
    </PageContainer>
  );
};

export default PersonaDetails;